import {
  DiscountCellData,
  DisplayContext,
  PartialPricingEngineOverrides,
  DiscountData,
} from '@frontend/api';

import {
  getDiscount as getDiscountFromStore,
  getCellsForDiscount,
} from 'modules/quotes/quoteItems/data/selectors';

import { PAGE_REQUEST } from 'store/app/types';
import { RootState } from 'store/configureStore';
import { ActionWithPayload } from 'store/types';

import { types } from './actions';

type DiscountModalState = {
  discountId: number | null;
  cells: DiscountCellData[];
  displayContext: DisplayContext[];
  isReadOnly: boolean;
  isOpen: boolean;
  overrides: PartialPricingEngineOverrides;
  liveDiscount: DiscountData | null;
};

const initialState: DiscountModalState = {
  discountId: null,
  cells: [],
  displayContext: [],
  isReadOnly: false,
  isOpen: false,
  overrides: {},
  liveDiscount: null,
};

const reducer = (
  state = initialState,
  action: ActionWithPayload<string, any>,
) => {
  const { payload } = action;
  switch (action.type) {
    case PAGE_REQUEST:
      return {
        ...initialState,
      };
    case types.OPEN_DISCOUNT_MODAL:
      return {
        ...state,
        discountId: payload.discount.id,
        cells: payload.cells,
        displayContext: payload.discount.displayContext,
        isOpen: true,
        isReadOnly: payload.isReadOnly,
        overrides: payload.discount.overrides,
      };
    case types.CLOSE_DISCOUNT_MODAL:
      return {
        ...state,
        discountId: null,
        cells: [],
        displayContext: [],
        isOpen: false,
        isReadOnly: false,
        overrides: {},
        liveDiscount: null,
      };
    case types.LIVE_DISCOUNT_PREVIEW_REQUEST:
      return {
        ...state,
        overrides: payload.overrides,
      };
    case types.LIVE_DISCOUNT_PREVIEW_SUCCESS:
      return {
        ...state,
        cells: payload.discountCells,
        displayContext: payload.displayContext,
        liveDiscount: payload,
      };
    case types.RECEIVE_OVERRIDES:
      return {
        ...state,
        overrides: payload.nextOverrides,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): DiscountModalState =>
  state.modules.quotes.discountModal;

export const getDiscountId = (state: RootState) => getScope(state).discountId;

export const getDiscount = (state: RootState) => {
  const discountId = getDiscountId(state);
  return discountId ? getDiscountFromStore(state, discountId) : undefined;
};

export const getCurrentOverrides = (state: RootState) =>
  getScope(state).overrides;

export const getDisplayContext = (state: RootState) =>
  getScope(state).displayContext;

export const getGlobalDiscountCells = (state: RootState) => {
  const discountId = getDiscountId(state);
  return discountId ? getCellsForDiscount(state, discountId) : [];
};

export const getIsDiscountModalOpen = (state: RootState) =>
  getScope(state).isOpen;

export const getIsReadOnly = (state: RootState) => getScope(state).isReadOnly;

export const getLocalDiscountCells = (state: RootState) =>
  getScope(state).cells;

export const getLiveDiscount = (state: RootState) =>
  getScope(state).liveDiscount;
